import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import CaseViewer from "@/components/CaseViewer";
import { useAuth } from "@/context/AuthContext";
import { useTriageWorklist } from "@/hooks/useTriageSiteWorklist";
import { SimulatedLabel } from "@/components/triage/SimulatedLabel";
import {
  adjacentCase,
  caseOrder,
  orderContextText,
  type TriageCaseNavState,
  type TriageWorklistRow,
} from "@/types/triageWorklist";

const STEP_BUTTON =
  "rounded-xs border border-border px-2 py-0.5 text-xs text-fg-muted hover:border-border-strong hover:text-fg disabled:opacity-60 disabled:hover:border-border disabled:hover:text-fg-muted";

/** The study's worklist context: exam, age, accession, and order context when set. */
function CaseContext({ row }: { row: TriageWorklistRow }) {
  const order = orderContextText(row);
  return (
    <div data-testid="triage-case-context" className="flex min-w-0 items-baseline gap-2">
      <span className="truncate text-xs font-medium text-fg">{row.exam ?? "—"}</span>
      <span className="font-mono text-[11px] text-fg-muted tabular-nums">
        {row.age_recorded ? `${row.age_years} y` : "age not recorded"}
      </span>
      <span className="font-mono text-[11px] text-fg-dim">{row.case_id}</span>
      {order && (
        <>
          <span data-testid="triage-case-order" className="text-[11px] text-fg-muted">
            {order}
          </span>
          <SimulatedLabel>Simulated</SimulatedLabel>
        </>
      )}
    </div>
  );
}

/**
 * One study opened from a site triage worklist. Routed studies open on their
 * device job (notification panel + key image); studies that routing kept away
 * from the device open on the images alone, with the routing reason. Prev / Next
 * step through the worklist in the order it was showing (wrapping at the ends).
 */
const TriageCasePage: React.FC = () => {
  const { worklistId, studyUid } = useParams<{ worklistId: string; studyUid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { data, isLoading, isError } = useTriageWorklist(token, worklistId);
  if (isLoading) return <p className="p-6 text-sm text-fg-muted">Loading case…</p>;
  const row = data?.studies.find((study) => study.study_uid === studyUid);
  if (isError || !data || !row)
    return (
      <p role="alert" className="p-6 text-sm text-fg-muted">
        This study is not on the worklist.
      </p>
    );

  const shown = (location.state as Partial<TriageCaseNavState> | null)?.order;
  const order = caseOrder(data.studies, row.study_uid, shown);
  const position = order.indexOf(row.study_uid) + 1;
  const go = (step: -1 | 1) => {
    const target = adjacentCase(order, row.study_uid, step);
    if (!target) return;
    navigate(`/triage/${encodeURIComponent(data.meta.id)}/case/${encodeURIComponent(target)}`, {
      state: { order } satisfies TriageCaseNavState,
    });
  };
  const canStep = order.length > 1;

  const studyId = row.image_study_id;
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border px-4 py-1.5 text-xs">
        <Link
          to={`/triage/${encodeURIComponent(data.meta.id)}`}
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          ← {data.meta.name}
        </Link>
        <CaseContext row={row} />
        <nav aria-label="Worklist cases" className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={!canStep}
            aria-label="Previous case"
            className={STEP_BUTTON}
          >
            ‹ Prev
          </button>
          <span className="font-mono text-[11px] text-fg-muted tabular-nums">
            Case {position} of {order.length}
          </span>
          <button
            type="button"
            onClick={() => go(1)}
            disabled={!canStep}
            aria-label="Next case"
            className={STEP_BUTTON}
          >
            Next ›
          </button>
        </nav>
      </div>
      <div className="min-h-0 flex-1">
        {studyId ? (
          <CaseViewer
            key={studyId}
            studyData={{
              studyId,
              triageDevice: row.routing.routed,
              device_id: row.routing.routed ? data.meta.device_model : undefined,
            }}
            disableReportQueries
            triageRouting={{
              device: data.meta.device_display_name,
              routed: row.routing.routed,
              reason: row.routing.reason,
              ageRecorded: row.age_recorded,
            }}
          />
        ) : (
          <p role="alert" className="p-6 text-sm text-fg-muted">
            Images are not available for this study.
          </p>
        )}
      </div>
    </div>
  );
};

export default TriageCasePage;
